/**
 * ImpactExtensionsPanel (C-9) — GMP impact assessment + containment + the
 * investigation-extension workflow, for the deviation detail screen.
 */
import { useState } from 'react';
import styles from './ImpactExtensionsPanel.module.css';
import {
  useDeviationImpact, useDeviationExtensionRequest, useDeviationExtensionDecide,
  type ImpactAssessment, type ImpactSeverity, type ExtensionRow,
} from './useDeviationDetailQuery';

const SEVERITIES: ImpactSeverity[] = ['NONE', 'LOW', 'MEDIUM', 'HIGH'];
const DIMENSIONS: { key: keyof Omit<ImpactAssessment, 'notes'>; label: string }[] = [
  { key: 'product', label: 'Product' },
  { key: 'patient', label: 'Patient / safety' },
  { key: 'dataIntegrity', label: 'Data integrity' },
  { key: 'regulatory', label: 'Regulatory' },
];
function sevClass(s: string) {
  return s === 'HIGH' ? styles.sevHigh : s === 'MEDIUM' ? styles.sevMedium : s === 'LOW' ? styles.sevLow : styles.sevNone;
}
function fmtDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' }); }
  catch { return String(iso); }
}

export function ImpactExtensionsPanel({
  deviationId, immediateActions, impact, extensions,
}: {
  deviationId: string;
  immediateActions: string | null;
  impact: ImpactAssessment;
  extensions: ExtensionRow[];
}) {
  const saveImpact = useDeviationImpact(deviationId);
  const requestExt = useDeviationExtensionRequest(deviationId);
  const decideExt = useDeviationExtensionDecide(deviationId);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<ImpactAssessment & { immediateActions: string; batches: string }>({
    ...impact,
    immediateActions: immediateActions ?? '',
    batches: '',
  });
  const [extOpen, setExtOpen] = useState(false);
  const [extDue, setExtDue] = useState('');
  const [extWhy, setExtWhy] = useState('');
  const [msg, setMsg] = useState<string | null>(null);

  async function saveImpactForm() {
    setMsg(null);
    const batches = form.batches.split(',').map((b) => b.trim()).filter(Boolean);
    try {
      await saveImpact.mutateAsync({
        immediateActions: form.immediateActions,
        impact: { product: form.product, patient: form.patient, dataIntegrity: form.dataIntegrity, regulatory: form.regulatory, notes: form.notes },
        ...(batches.length ? { affectedBatchRefs: batches } : {}),
      });
      setEditing(false);
    } catch (e: unknown) { setMsg(e instanceof Error ? e.message : 'Could not save the impact assessment.'); }
  }
  async function submitExtension() {
    setMsg(null);
    if (!extDue) { setMsg('Pick a requested new due date.'); return; }
    if (!extWhy.trim()) { setMsg('A justification is required.'); return; }
    try { await requestExt.mutateAsync({ requestedDue: extDue, justification: extWhy.trim() }); setExtOpen(false); setExtDue(''); setExtWhy(''); }
    catch (e: unknown) { setMsg(e instanceof Error ? e.message : 'Could not request the extension.'); }
  }
  async function decide(extensionId: string, decision: 'APPROVED' | 'REJECTED') {
    setMsg(null);
    try { await decideExt.mutateAsync({ extensionId, decision }); }
    catch (e: unknown) { setMsg(e instanceof Error ? e.message : 'Could not record the decision.'); }
  }

  return (
    <>
      <section className={styles.panel} aria-labelledby="dev-impact">
        <header className={styles.head}>
          <h2 id="dev-impact" className={styles.title}>Impact assessment &amp; containment</h2>
          {!editing && <button type="button" className={styles.action} onClick={() => { setForm({ ...impact, immediateActions: immediateActions ?? '', batches: '' }); setEditing(true); setMsg(null); }}>Edit</button>}
        </header>
        {msg && <div className={styles.err} role="alert">{msg}</div>}
        {!editing ? (
          <>
            <div className={styles.sevGrid}>
              {DIMENSIONS.map((d) => (
                <div key={d.key} className={styles.sevCard}>
                  <span className={styles.sevLabel}>{d.label}</span>
                  <span className={`${styles.sevPill} ${sevClass(impact[d.key])}`}>{impact[d.key]}</span>
                </div>
              ))}
            </div>
            <div className={styles.block}><strong>Immediate actions</strong><p>{immediateActions || '— not recorded —'}</p></div>
            {impact.notes && <div className={styles.block}><strong>Impact rationale</strong><p>{impact.notes}</p></div>}
          </>
        ) : (
          <div className={styles.form}>
            <div className={styles.sevGrid}>
              {DIMENSIONS.map((d) => (
                <label key={d.key} className={styles.sevEdit}>
                  <span className={styles.sevLabel}>{d.label}</span>
                  <select value={form[d.key]} onChange={(e) => setForm({ ...form, [d.key]: e.target.value as ImpactSeverity })}>
                    {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
              ))}
            </div>
            <label className={styles.field}><span>Immediate actions</span>
              <textarea rows={2} value={form.immediateActions} onChange={(e) => setForm({ ...form, immediateActions: e.target.value })} /></label>
            <label className={styles.field}><span>Impact rationale</span>
              <textarea rows={2} value={form.notes ?? ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></label>
            <label className={styles.field}><span>Affected batches (comma-separated; leave blank to keep)</span>
              <input type="text" value={form.batches} onChange={(e) => setForm({ ...form, batches: e.target.value })} placeholder="PARA-500-2511, PARA-500-2512" /></label>
            <div className={styles.formActions}>
              <button type="button" className={styles.btnNeutral} onClick={() => setEditing(false)}>Cancel</button>
              <button type="button" className={styles.btnPrimary} onClick={() => void saveImpactForm()} disabled={saveImpact.isPending}>
                {saveImpact.isPending ? 'Saving…' : 'Save assessment'}
              </button>
            </div>
          </div>
        )}
      </section>

      <section className={styles.panel} aria-labelledby="dev-ext">
        <header className={styles.head}>
          <h2 id="dev-ext" className={styles.title}>Investigation extensions</h2>
          <span className={styles.count}>{extensions.length}</span>
          {!extOpen && <button type="button" className={styles.action} onClick={() => { setExtOpen(true); setMsg(null); }}>Request extension</button>}
        </header>
        {extOpen && (
          <div className={styles.form}>
            <div className={styles.row2}>
              <label className={styles.field}><span>New due date</span>
                <input type="date" value={extDue} onChange={(e) => setExtDue(e.target.value)} /></label>
            </div>
            <label className={styles.field}><span>Justification</span>
              <textarea rows={2} value={extWhy} onChange={(e) => setExtWhy(e.target.value)} placeholder="Why the investigation needs more time…" /></label>
            <div className={styles.formActions}>
              <button type="button" className={styles.btnNeutral} onClick={() => setExtOpen(false)}>Cancel</button>
              <button type="button" className={styles.btnPrimary} onClick={() => void submitExtension()} disabled={requestExt.isPending}>
                {requestExt.isPending ? 'Requesting…' : 'Request extension'}
              </button>
            </div>
          </div>
        )}
        {extensions.length === 0 && !extOpen ? (
          <div className={styles.empty}>No extension requests.</div>
        ) : (
          <ul className={styles.extList}>
            {extensions.map((e) => (
              <li key={e.extensionId} className={styles.extRow}>
                <span className={`${styles.extStatus} ${e.status === 'APPROVED' ? styles.extApproved : e.status === 'REJECTED' ? styles.extRejected : styles.extPending}`}>{e.status}</span>
                <span className={styles.extMeta}>
                  New due <strong>{fmtDate(e.requestedDueUtc)}</strong> — {e.justification}
                  <span className={styles.sub}> · requested by {e.requestedByName ?? 'user'}{e.decidedByName ? ` · decided by ${e.decidedByName}` : ''}</span>
                </span>
                {e.status === 'PENDING' && (
                  <span className={styles.extActions}>
                    <button type="button" className={styles.btnPrimary} onClick={() => void decide(e.extensionId, 'APPROVED')} disabled={decideExt.isPending}>Approve</button>
                    <button type="button" className={styles.btnNeutral} onClick={() => void decide(e.extensionId, 'REJECTED')} disabled={decideExt.isPending}>Reject</button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
